import { CmsAppearanceHint } from "@/components/cms/theme/CmsAppearanceHint";
import { CmsColorPickerField } from "@/components/cms/theme/CmsColorPickerField";
import {
  CMS_PALETTE_CONTROL_ROW_CLASS,
  CMS_PALETTE_HEX_INPUT_CLASS,
} from "@/components/cms/theme/cms-color-input-styles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { useEnsurePaletteColor } from "@/hooks/useEnsurePaletteColor";
import type { CmsColor } from "@/lib/cms/cms-theme.constants";
import {
  CMS_FORM_SELECT_TRIGGER_CLASS,
  isValidHex,
  resolveColor,
} from "@/lib/cms/cms-theme.constants";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  label: string;
  colors: CmsColor[];
  value: string | null | undefined;
  onChange: (colorId: string | null) => void;
  allowNone?: boolean;
  noneLabel?: string;
  triggerClassName?: string;
  onColorsChange?: (colors: CmsColor[]) => void;
};

export function PaletteColorPicker({
  label,
  colors,
  value,
  onChange,
  allowNone = false,
  noneLabel = "—",
  triggerClassName,
  onColorsChange,
}: Props) {
  const { t } = useTranslation();
  const { ensurePaletteColor } = useEnsurePaletteColor();
  const [hexDraft, setHexDraft] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const resolved = resolveColor(colors, value, "#FFFFFF");
  const hasPalette = colors.length > 0;

  const applyHex = async (rawHex: string) => {
    const hex = rawHex.trim().toUpperCase();
    if (!isValidHex(hex)) return;

    const existing = colors.find((c) => c.hex.toUpperCase() === hex);
    if (existing) {
      onChange(existing.id);
      setHexDraft(hex);
      return;
    }

    setIsAdding(true);
    try {
      const { id, colors: nextColors } = await ensurePaletteColor(hex);
      onColorsChange?.(nextColors);
      onChange(id);
      setHexDraft(hex);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-slate-700">{label}</label>

      {!hasPalette ? (
        <div className="max-w-prose rounded-lg border border-dashed border-slate-300 bg-slate-50/80 p-4 space-y-3">
          <p className="text-sm font-medium leading-relaxed text-slate-700">
            {t("cms.palette.emptyHint")}
          </p>
          <div className={CMS_PALETTE_CONTROL_ROW_CLASS}>
            <CmsColorPickerField
              value={resolved}
              disabled={isAdding}
              aria-label={t("cms.palette.pickColor")}
              onCommit={(hex) => void applyHex(hex)}
            />
            <input
              type="text"
              value={hexDraft}
              disabled={isAdding}
              placeholder="#000000"
              spellCheck={false}
              className={CMS_PALETTE_HEX_INPUT_CLASS}
              onChange={(e) => setHexDraft(e.target.value)}
              onBlur={() => {
                if (hexDraft) void applyHex(hexDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && hexDraft) void applyHex(hexDraft);
              }}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {allowNone && (
              <button
                type="button"
                disabled={isAdding}
                title={noneLabel}
                onClick={() => onChange(null)}
                className={cn(
                  "h-9 min-w-9 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-500 transition-shadow hover:ring-2 hover:ring-slate-300",
                  value == null && "ring-2 ring-slate-400",
                )}
              >
                {noneLabel}
              </button>
            )}
            {colors.map((color) => (
              <button
                key={color.id}
                type="button"
                disabled={isAdding}
                title={color.name}
                onClick={() => onChange(color.id)}
                className={cn(
                  "h-9 w-9 shrink-0 rounded-md border border-slate-200 transition-shadow hover:ring-2 hover:ring-slate-300",
                  value === color.id && "ring-2 ring-slate-500",
                )}
                style={{ backgroundColor: color.hex }}
              />
            ))}
          </div>

          <div
            className={cn(
              CMS_PALETTE_CONTROL_ROW_CLASS,
              "flex-wrap rounded-lg border border-slate-200 bg-white p-3",
            )}
          >
            <CmsColorPickerField
              value={resolved}
              disabled={isAdding}
              aria-label={t("cms.palette.otherColor")}
              onCommit={(hex) => void applyHex(hex)}
            />
            <input
              type="text"
              value={hexDraft || resolved}
              disabled={isAdding}
              placeholder="#000000"
              spellCheck={false}
              className={CMS_PALETTE_HEX_INPUT_CLASS}
              onChange={(e) => setHexDraft(e.target.value)}
              onBlur={() => {
                if (hexDraft) void applyHex(hexDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && hexDraft) void applyHex(hexDraft);
              }}
            />
            <Select
              value={value ?? (allowNone ? "__none__" : undefined)}
              onValueChange={(v) => onChange(v === "__none__" ? null : v)}
              disabled={isAdding}
            >
              <SelectTrigger
                className={cn(
                  "min-w-[160px] flex-1",
                  CMS_FORM_SELECT_TRIGGER_CLASS,
                  triggerClassName,
                )}
              >
                <SelectValue placeholder={t("cms.palette.selectByName")} />
              </SelectTrigger>
              <SelectContent>
                {allowNone && (
                  <SelectItem value="__none__">{noneLabel}</SelectItem>
                )}
                {colors.map((color) => (
                  <SelectItem key={color.id} value={color.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block h-4 w-4 rounded border"
                        style={{ backgroundColor: color.hex }}
                      />
                      {color.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <CmsAppearanceHint size="sm">{t("cms.palette.seamlessHint")}</CmsAppearanceHint>
    </div>
  );
}
